import { useRouteMatch } from 'react-router-dom';
import useSWR, { Fetcher, SWRResponse, useSWRConfig } from 'swr';
import { BareFetcher, PublicConfiguration } from 'swr/dist/types';

import { KlassesShowResponse as Response } from '@gmm/sdk/classes';
import { is404 } from '~/lib/config/axios';
import { useCurrentTeacher } from '~/lib/hooks/useCurrentTeacher';
import { useSnackbar } from '~/lib/hooks/useSnackbar';
import { useMigratedEndpoint } from '~/lib/hooks/voodoo';
import { ReadOnlyOptions } from '~/lib/sdk';
import { sdkFetcher, swrKeyFor, voodooSdkFetcher } from '~/lib/swr';

export type UseGetClassDetail = SWRResponse<Response, unknown>;

export const useGetClassDetail = ({
  readOnly,
}: ReadOnlyOptions = {}): UseGetClassDetail => {
  const { onErrorRetry } = useSWRConfig();
  const { enqueueGenericError } = useSnackbar();
  const { data: teacher } = useCurrentTeacher();
  const { classId } =
    useRouteMatch<{ classId?: string }>('/classes/:classId')?.params || {};

  const isVoodooEndpoint = useMigratedEndpoint('classes-show');
  const fetcher = isVoodooEndpoint ? voodooSdkFetcher : sdkFetcher;
  const swrKey = swrKeyFor(() => {
    if (isVoodooEndpoint) {
      return teacher && classId ? ['classes.show', teacher.id, classId] : null;
    } else {
      return classId ? ['classes.show', classId] : null;
    }
  });

  return useSWR<Response>(swrKey, {
    fetcher: fetcher as BareFetcher<Response>,
    onError: error => {
      if (is404(error)) return;

      enqueueGenericError();
    },

    // This maybe should become our default behavior. But I want to audit some
    // things before we make that decision
    onErrorRetry: (error, key, config, ...rest) => {
      // Received not found, so don't retry
      if (is404(error)) return;

      return onErrorRetry(
        error,
        key,
        // This is pretty terrible, but the type can't be un-narrowed, for whatever reason
        config as Readonly<PublicConfiguration<any, any, Fetcher>>,
        ...rest,
      );
    },
    revalidateIfStale: !readOnly,
    revalidateOnFocus: !readOnly,
    revalidateOnReconnect: !readOnly,
  }) as UseGetClassDetail;
};
